import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { AuthModel, BaseAuthResponse } from '../_models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { UsersTable } from 'src/app/_fake/fake-db/users.table';
import { NgxSpinnerService } from 'ngx-spinner';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<AuthModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<AuthModel>;
  isLoadingSubject: BehaviorSubject<boolean>;


  get currentUserValue(): AuthModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: AuthModel) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,private spinner: NgxSpinnerService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<AuthModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  // public methods
  login(passportNo: string, password: string): Observable<AuthModel | any> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.login(passportNo, password).pipe(
      map((auth: BaseAuthResponse) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(err);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  Stafflogin(personalNo: string, password: string): Observable<AuthModel | any> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.Stafflogin(personalNo, password).pipe(
      map((auth: BaseAuthResponse) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(err);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  CreateNewAccount(PassportNo,Mobile,Password,SecretKey)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.CreateNewAccount(PassportNo,Mobile,Password,SecretKey).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  CreateNewAccountForGuest(apiGuestInfoModel)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.CreateNewAccountForGuest(apiGuestInfoModel).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  ForgetPassword(PassportNo,Mobile)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.ForgetPassword(PassportNo,Mobile).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  SendOtpForPatient(PassportNo,Mobile)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.SendOtpForPatient(PassportNo,Mobile).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  CheckPatientDataAndSendOTP(PassportNo,Mobile)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.CheckPatientDataAndSendOTP(PassportNo,Mobile).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  SendOtpForGuest(PassportNo,Mobile)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.SendOtpForGuest(PassportNo,Mobile).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  ValidateOtp(PassportNo: string,Otp: string, SecretKey:string)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.ValidateOtp(PassportNo,Otp,SecretKey).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  ResetPassword(PatientId,GuestId ,NewPassword,SecretKey)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.ResetPassword(PatientId,GuestId,NewPassword,SecretKey).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  ChangePassword(TokenRequestModel)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.ChangePassword(TokenRequestModel).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  ChangePasswordForStaff(TokenRequestModel)
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.ChangePasswordForStaff(TokenRequestModel).pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  DeleteAccount()
  {
    this.isLoadingSubject.next(true);
    return this.authHttpService.DeleteAccount().pipe(
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // public methods
  RefreshToken(PatientId: number, GuestId : number,RefreshToken:string): Observable<AuthModel | any> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.RefreshToken(PatientId, GuestId,RefreshToken).pipe(
      map((auth: BaseAuthResponse) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(err);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  SwitchFamilyMember(PatientId: number): Observable<AuthModel | any> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.SwitchFamilyMember(PatientId).pipe(
      map((auth: BaseAuthResponse) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(err);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    sessionStorage.clear()
    localStorage.removeItem(this.authLocalStorageToken);
    localStorage.removeItem("RefreshToken");
    if (localStorage.getItem("Service") == 'BookAppointment')
    {
      this.router.navigate(['/book-appointment']);
    }
    else
    {
      this.router.navigate(['/'], {
        queryParams: {},
      });
    }
  

    // if (sessionStorage.getItem("Service") == 'BookAppointment') {
    //     this.router.navigate(['/book-appointment']);
    //     localStorage.removeItem(this.authLocalStorageToken);
    //     localStorage.removeItem("RefreshToken");
    // }
    // else
    // {
    //   sessionStorage.clear()
    //   localStorage.removeItem(this.authLocalStorageToken);
    //   localStorage.removeItem("RefreshToken");
    //   this.router.navigate(['/'], {
    //     queryParams: {},
    //   });
    // }
  }

  getUserByToken(): Observable<AuthModel> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.token) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.getUserByTokenser(auth).pipe(
      map((user: AuthModel) => {
        if (user) {
          this.currentUserSubject = new BehaviorSubject<AuthModel>(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
    
    if (auth) {
      ;
      this.currentUserSubject = new BehaviorSubject<AuthModel>(auth);
    } else {
      this.logout();
    }
    return this.currentUserSubject;
  }
  getUserByTokenser(useral: any): Observable<AuthModel> {
    return of(useral);
  }
 
  // private methods
  private setAuthFromLocalStorage(auth: BaseAuthResponse): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.isSuccess && auth.data) {
       //localStorage.removeItem(this.authLocalStorageToken);
       localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth.data));
       //localStorage.removeItem("RefreshToken");
       localStorage.setItem("RefreshToken", JSON.stringify(auth.data.refreshToken));

      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel {
    try {
      const authData = JSON.parse(
        localStorage.getItem(this.authLocalStorageToken)
      );
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
  showLoader() {
    this.spinner.show();
  }
  hideLoader() {
    this.spinner.hide();
  }
}
